import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Banknote } from "lucide-react";

const fmt = (v, cur) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: cur || "GBP", notation: "compact", maximumFractionDigits: 1 }).format(v || 0);

const three = (t) => (t ? `${t.min}–${t.max} (≈${t.likely})` : "—");

export const FairRiskPanel = ({ riskId }) => {
  const [data, setData] = useState(null);
  const [mit, setMit] = useState(null);

  useEffect(() => {
    if (!riskId) return;
    let active = true;
    api.get(`/fair/risks/${riskId}`).then(({ data }) => active && setData(data)).catch(() => {});
    api.get(`/fair/risks/${riskId}/mitigation?reduction=0.4`).then(({ data }) => active && setMit(data)).catch(() => {});
    return () => {
      active = false;
    };
  }, [riskId]);

  if (!data) return null;
  const cur = data.currency;

  return (
    <div className="mt-6 rounded-md border border-ek-purple/15 bg-gradient-to-br from-ek-purple/[0.05] to-transparent p-4" data-testid="fair-risk-panel">
      <div className="flex items-center justify-between">
        <h4 className="flex items-center gap-1.5 font-display text-sm font-semibold text-ek-ink">
          <Banknote size={14} className="text-ek-purple" /> Financial exposure (FAIR)
        </h4>
        <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ek-ink/65">
          {data.source}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="rounded bg-white px-3 py-2">
          <p className="text-[11px] text-ek-ink/65">Expected annual loss</p>
          <p className="font-display text-xl font-bold text-ek-ink" data-testid="fair-risk-mean">{fmt(data.summary.mean, cur)}</p>
        </div>
        <div className="rounded bg-white px-3 py-2">
          <p className="text-[11px] text-ek-ink/65">P90 (worst-case)</p>
          <p className="font-display text-xl font-bold text-ek-ink">{fmt(data.summary.p90, cur)}</p>
        </div>
      </div>

      <div className="mt-3 space-y-1 text-[11px] text-ek-ink/65">
        <div className="flex justify-between"><span>Loss events / year</span><span className="tabular-nums">{three(data.inputs.lef)}</span></div>
        <div className="flex justify-between"><span>Primary loss / event ({cur})</span><span className="tabular-nums">{three(data.inputs.primary_loss)}</span></div>
      </div>

      {mit && (
        <p className="mt-3 border-t border-black/5 pt-2 text-[11px] text-ek-ink/60" data-testid="fair-mitigation">
          A further <strong>40%</strong> control reduction would lower expected loss by{" "}
          <strong className="text-emerald-700">{fmt(mit.annual_risk_reduction, cur)}/yr</strong>.
        </p>
      )}
    </div>
  );
};
